<template>
  <div
    :id="sliceId"
    class="oct-slice-view slice-image-wrapper interactive-canvas-wrapper"
    :class="cursorClass"
    :style="style"
    @mousedown.self.left="handleMousedown"
    @mousemove.self="handleMousemove"
    @mouseup.self.left="handleMouseup"
    @mouseout.self="handleMouseout"
    v-hammer:press="handlePanstart"
    v-hammer:panstart="handlePanstart"
    v-hammer:pan="handlePanmove"
    v-hammer:panend="handlePanend"
    v-hammer:tap="handleTap">
    <image-not-found v-if="imgNotFound" />
    <pixel-shaded-canvas
      :img="img"
      :octColorSettings="octColorSettings"
      :canvasSize="fitSize"
      :imgSize="imgSize"
      :transform="transform"
      :flippedX="flippedX"
      :isFlipped90="isFlipped90"
      :isFlipped270="isFlipped270"/>
    <canvas-annotation
      v-if="showSegmentation"
      :canvasSize="fitSize"
      :imgSize="imgSize"
      :transform="transform"
      :annotation="segmentation"
      :flippedX="flippedX"
      :isFlipped90="isFlipped90"
      :isFlipped270="isFlipped270"/>
    <canvas-annotation
      v-if="isMeasuring"
      :canvasSize="fitSize"
      :annotation="segmentationMeasurement"
      :isFlipped90="isFlipped90"
      :isFlipped270="isFlipped270"/>
    <canvas-annotation
      v-if="!!otherLine"
      :canvasSize="fitSize"
      :annotation="otherLine"
      :imgSize="imgSize"
      :flippedX="flippedX"
      :isFlipped90="isFlipped90"
      :isFlipped270="isFlipped270"/>
    <canvas-annotation
      v-if="showOpticDiscLine"
      :canvasSize="fitSize"
      :annotation="opticDiscLine"
      :imgSize="imgSize"
      :flippedX="flippedX"
      :isFlipped90="isFlipped90"
      :isFlipped270="isFlipped270"/>
    <canvas-annotation :canvasSize="fitSize" :annotation="eyeMarker" :forceTranslate="this.eyeMarkerForceTranslate" />
    <v-progress-circular v-if="isLoading" class="escalon-absolute-center" indeterminate />
  </div>
</template>

<script>
import pixelShadedCanvas from "../../../canvas/components/PixelShadedCanvas";
import canvasAnnotation from "../../../canvas/components/CanvasAnnotation";
import SegmentationMeasurement from "../../../canvas/annotations/segmentationMeasurement";
import Segmentation from "../../../canvas/annotations/segmentation";
import EyeMarker from "../../../canvas/annotations/eyeMarker";
import OtherLine from "../../../canvas/annotations/otherLine";
import OpticDiscLine from "../../../canvas/annotations/opticDiscLine";
import touchMouseEvents from "../../mixins/TouchMouseEvents";
import imageNotFound from "../../misc/ImageNotFound";
export default {
  name: "octSliceView",
  mixins: [touchMouseEvents],
  components: { canvasAnnotation, pixelShadedCanvas, imageNotFound },
  props: {
    sliceSize: Object,
    img: HTMLImageElement,
    imgSize: Object,
    octColorSettings: Object,
    fitSize: Object,
    transform: Object,
    imgLoading: Boolean,
    dataLoading: Boolean,
    imgNotFound: Boolean,
    sliceEyeMarker: Number,
    sliceInfo: Object,
    layerData: Array,
    measureData: Object,
    strechSegmentation: Boolean,
    flippedX: Boolean,
    isFlipped90: Boolean,
    isFlipped270: Boolean,
    otherIndex: Number,
    otherCount: Number,
    otherColor: String,
    eyeMarkerAngle: { type: Number, default: 0 },
    sliceId: { type: String, default: "slice-image-wrapper" },
    showSegmentation: Boolean,
    isMeasuring: Boolean,
    realLocation: Function,
    isInBounds: Function,
    showOpticDiscLine: Boolean,
    cscans: Array,
    cscanCurrent: Object,
    cscanCurrentIndex: Number,
    eyeMarkerForceTranslate: Boolean
  },
  data() {
    return {
      segmentation: new Segmentation(),
      segmentationMeasurement: new SegmentationMeasurement(),
      eyeMarker: new EyeMarker(),
      otherLine: null,
      opticDiscLine: new OpticDiscLine(),
      cursorClass: ""
    };
  },
  computed: {
    isLoading() {
      return this.imgLoading || this.dataLoading;
    },
    style() {
      if (this.sliceSize)
        return {
          height: this.sliceSize.h + "px"
        };
      return null;
    }
  },
  methods: {
    setupOtherLine() {
      if (
        this.otherCount &&
        this.otherColor &&
        (this.otherIndex || this.otherIndex === 0)
      ) {
        if (!this.otherLine) this.otherLine = new OtherLine();
        this.otherLine.index = this.otherIndex;
        this.otherLine.count = this.otherCount;
        this.otherLine.color = this.otherColor;
        this.otherLine.signalReady();
      } else {
        this.otherLine = null;
      }
    },
    handleInteractionStart(e) {
      if (!this.isMeasuring && !this.showOpticDiscLine) return;
      this.setMousedown();
      if (this.isMeasuring)  {
        let { x, y } = this.realLocation(e.offsetX, e.offsetY);
        if (!this.isInBounds(x, y)) return;
        this.$emit("measurementChanged", x);
      }
      else {
        let { x, y } = this.realLocation(e.offsetX, e.offsetY, this.isFlipped270);
        if (!this.isInBounds(x, y)) return;
        if (this.isFlipped270)
          this.$emit("selectCScan", x / this.fitSize.w);
        else
          this.$emit("selectCScan", y / this.fitSize.h);
      }
    },
    handleHover(e){
      this.handleInteractionMove(e);
    },
    handleInteractionMove(e) {
      if ((!this.isMeasuring && !this.showOpticDiscLine) || !this.realLocation || !this.isInBounds) return;
      this.cursorClass = "";
      if (this.isMeasuring) {
        let { x, y } = this.realLocation(e.offsetX, e.offsetY);
        if (!this.isInBounds(x, y)) this.setMouseup();
        if (this.isMousedown) {
          this.$emit("measurementChanged", x);
          this.cursorClass = "dragging-active";
        } else {
          this.cursorClass = this.segmentationMeasurement.getHoverClass(x, y);
        }
      }
      else if (this.showOpticDiscLine) {
        let { x, y } = this.realLocation(e.offsetX, e.offsetY, this.isFlipped270);
        if (!this.isInBounds(x, y)) this.setMouseup();
        if (this.isMousedown) {
          if (this.isFlipped270)
            this.$emit("selectCScan", x / this.fitSize.w);
          else
            this.$emit("selectCScan", y / this.fitSize.h);
          this.cursorClass = "dragging-active";
        }
      }
    }
  },
  watch: {
    sliceEyeMarker() {
      this.eyeMarker.updateMarker(this.sliceEyeMarker);
    },
    sliceInfo() {
      this.segmentation.updateSliceInfo(this.sliceInfo);
      this.segmentationMeasurement.updateSliceInfo(this.sliceInfo);
    },
    layerData() {
      this.segmentation.updateLayerData(this.layerData);
    },
    fitSize() {
      this.segmentationMeasurement.updateSliceFit(this.fitSize);
    },
    measureData() {
      this.segmentationMeasurement.updateMeasureData(this.measureData);
    },
    transform: {
      handler() {
        this.segmentationMeasurement.updateTransform(this.transform);
      },
      deep: true
    },
    otherIndex() {
      this.setupOtherLine();
    },
    otherCount() {
      this.setupOtherLine();
    },
    otherColor() {
      this.setupOtherLine();
    },
    eyeMarkerAngle() {
      this.eyeMarker.updateTheta(this.eyeMarkerAngle);
    },
    cscans() {
      if (this.showOpticDiscLine)
        this.opticDiscLine.updateCScans(this.cscans);
    },
    cscanCurrent() {
      if (this.showOpticDiscLine)
        this.opticDiscLine.updateCScanIndex(this.cscanCurrentIndex);
    }
  },
  mounted() {
    this.eyeMarker.updateMarker(this.sliceEyeMarker);
    this.eyeMarker.updateTheta(this.eyeMarkerAngle);
    this.segmentation.updateSliceInfo(this.sliceInfo);
    this.segmentation.updateLayerData(this.layerData);
    this.segmentation.updateStretchSegmentation(this.strechSegmentation);
    this.segmentationMeasurement.updateMeasureData(this.measureData);
    this.segmentationMeasurement.updateTransform(this.transform);
    this.segmentationMeasurement.updateSliceInfo(this.sliceInfo);
    this.segmentationMeasurement.updateSliceFit(this.fitSize);
    this.setupOtherLine();
    if (this.showOpticDiscLine)
      this.opticDiscLine.updateCScans(this.cscans);
  }
};
</script>

<style lang="scss" scoped>
.oct-slice-view {
  position: relative;
  height: 100%;
}
</style>
